/**
 * ChallengeParticipantSwitcher
 * Dropdown to pick which participant's picks are shown as "opponent" in the group challenge card.
 * List is rendered in a Modal so it appears on top of the card; position is measured from the trigger.
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Modal,
  useWindowDimensions,
} from 'react-native';
import { ChevronUp, ChevronDown } from 'lucide-react-native';
import { CHALLENGE_CARD } from '../../../src/challenges/constants/challenges.constants';
import { getChallengeParticipantSwitcherStyles } from './ChallengeParticipantSwitcher.styles';
import { useTheme } from '../../../src/theme/useTheme';
import type { Participant } from '../../../src/challenges/types/challengeCard.types';

const PLACEHOLDER = 'Select participant';

export interface ChallengeParticipantSwitcherProps {
  /** Other participants only (exclude current user). */
  participants: Participant[];
  selectedOpponentId: string | null;
  onSelectParticipant: (userId: string) => void;
  testID?: string;
}

export const ChallengeParticipantSwitcher: React.FC<ChallengeParticipantSwitcherProps> = ({
  participants,
  selectedOpponentId,
  onSelectParticipant,
  testID,
}) => {
  const theme = useTheme();
  const { width: screenWidth } = useWindowDimensions();
  const styles = getChallengeParticipantSwitcherStyles(theme, screenWidth);
  const [open, setOpen] = useState(false);
  const [listLayout, setListLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const triggerRef = useRef<View>(null);

  const selectedName =
    participants.find((p) => p.id === selectedOpponentId)?.name ?? PLACEHOLDER;
  const isPlaceholder = !selectedOpponentId;

  const handleOpen = useCallback(() => {
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      setListLayout({ x, y: y + height + 4, width, height });
      setOpen(true);
    });
  }, []);

  const handleSelect = useCallback(
    (userId: string) => {
      onSelectParticipant(userId);
      setOpen(false);
      setListLayout(null);
    },
    [onSelectParticipant]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setListLayout(null);
  }, []);

  if (participants.length === 0) return null;

  return (
    <View style={styles.row} testID={testID ?? 'challenge-participant-switcher'}>
      <Text style={styles.label} numberOfLines={1}>
        {CHALLENGE_CARD.VIEW_PICKS_LABEL}
      </Text>
      <View ref={triggerRef} style={[styles.dropdownWrap, open && styles.dropdownWrapOpen]}>
        <Pressable
          style={[styles.dropdownTrigger, open && styles.dropdownTriggerOpen]}
          onPress={() => (open ? handleClose() : handleOpen())}
          testID={testID ? `${testID}-trigger` : undefined}
        >
          <Text
            style={[
              styles.dropdownTriggerText,
              isPlaceholder && styles.dropdownTriggerPlaceholder,
            ]}
            numberOfLines={1}
          >
            {selectedName}
          </Text>
          {open
            ? <ChevronUp size={22} color={theme.gray[500]} />
            : <ChevronDown size={22} color={theme.gray[500]} />
          }
        </Pressable>
      </View>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={handleClose}
      >
        <Pressable style={styles.modalBackdrop} onPress={handleClose}>
          {listLayout != null && (
            <Pressable
              style={[
                styles.dropdownListModal,
                {
                  left: listLayout.x,
                  top: listLayout.y,
                  width: listLayout.width,
                },
              ]}
              onPress={(e) => e.stopPropagation()}
            >
              <ScrollView
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={true}
                bounces={false}
              >
                {participants.map((p, index) => {
                  const isSelected = p.id === selectedOpponentId;
                  const isLast = index === participants.length - 1;
                  return (
                    <Pressable
                      key={p.id}
                      style={[styles.dropdownItem, isLast && styles.dropdownItemLast]}
                      onPress={() => handleSelect(p.id)}
                      testID={testID ? `${testID}-item-${p.id}` : undefined}
                    >
                      <Text
                        style={[
                          styles.dropdownItemText,
                          isSelected && styles.dropdownItemTextSelected,
                        ]}
                        numberOfLines={1}
                      >
                        {p.name}
                      </Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </Pressable>
          )}
        </Pressable>
      </Modal>
    </View>
  );
};
